import React, { useEffect, useState, useRef } from 'react';
import random from 'lodash/random';
import cn from 'classnames';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import Button, { ILink } from 'tbk-components/src/components/Button';
import Image, { IImage } from 'tbk-components/src/components/Image';
import MMNav, { MMNavItem } from '../../common/MegaMenus/Nav';
import Nav, { NavItem } from 'tbk-components/src/components/Nav';
import Collapse from 'bootstrap/js/dist/collapse';
import Offcanvas from 'bootstrap/js/dist/offcanvas';

// Represents a link in the language selector drop-down
export type LanguageNavItem = {
	href: string;
	label: string;
	target?: string;
	icon?: string;
	code?: string;
	locale?: string;
};

export interface IHeaderProps extends IBaseElementProps {
	logo?: IImage;
	logoURL?: string;
	headerBusinessUnit: string;
	businessUnitURL?: string;
	mainNav?: NavItem[];
	loginNav?: NavItem[];
	profileImage?: IImage;
	profileLink?: ILink;
	showAllButton?: ILink;
	searchButton?: ILink;
	activeLanguage?: LanguageNavItem;
	languageSelectionNav: LanguageNavItem[];
	homeURL?: string;
	homeURLText?: string;
	registerURL?: string;
	registerURLText?: string;
	topMenuNav?: MMNavItem[];
	/*
	 * Will turn the header items white when stickied on desktop.
	 */
	invert?: boolean;
	/*
	 * Will turn the button color to secondary, for example.
	 */
	styleInvert?: boolean;
}

const CareersHeader: React.FC<IHeaderProps> = ({
	id,
	className,
	classNames = [],
	logo,
	logoURL,
	headerBusinessUnit,
	businessUnitURL,
	topMenuNav,
	mainNav,
	profileImage,
	profileLink,
	loginNav,
	showAllButton,
	searchButton,
	homeURL,
	homeURLText = 'Go to Calian Home',
	registerURL,
	registerURLText = 'Create an account',
	activeLanguage,
	languageSelectionNav,
	invert = false,
	styleInvert = false,
}) => {
	// Start of sticky positioning, collapse and off canvas code taken from default ba3e Navbar functionality.
	const [navLanguage, setNavLanguage] = useState('en');

	const ref = useRef(null);
	const collapseRef = useRef(null);
	const offcanvasRef = useRef(null);

	useEffect(() => {
		let currentPageLanguage = document.documentElement.lang;
		setNavLanguage(currentPageLanguage);

		if (!ref.current) {
			return;
		}

		// @ts-ignore
		const collapse = new Collapse(collapseRef.current, {
			toggle: false,
		});

		// @ts-ignore
		const offCanvas = new Offcanvas(offcanvasRef.current);

		const navbar = ref.current! as HTMLDivElement;

		const alertBar = document.querySelector(
			'[data-component="AlertBar"]',
		) as HTMLDivElement;

		if (alertBar) {
			document.body.classList.add('alert-bar-visible');
		}

		const handleScroll = () => {
			if (window.scrollY > navbar.offsetTop) {
				document.body.classList.add('careers-navbar-sticky');
			} else {
				document.body.classList.remove('careers-navbar-sticky');
			}
		};

		const mainDesktopNav = ref.current.querySelector(
			'.calian-header__main-nav',
		);

		const mainDesktopOverflowNav = ref.current.querySelector(
			'.calian-header__main-nav--overflow',
		);

		const createOverflowDropdown = () => {
			let maxHeight = 40;
			let mainDesktopNavHeight = mainDesktopNav.clientHeight;
			if (mainDesktopNavHeight > maxHeight) {
				mainDesktopOverflowNav.classList.remove('d-none');

				while (mainDesktopNavHeight > maxHeight) {
					const lastChild = mainDesktopNav.lastElementChild;

					if (lastChild.classList.contains('dropdown')) {
						lastChild.addEventListener('click', (e) =>
							e.stopPropagation(),
						);
						const overflowCollapseID = `overflowCollapse-${random(
							100,
						)}`;
						const navLink = lastChild.querySelector(
							'.nav-link',
						) as HTMLAnchorElement;

						navLink.setAttribute('href', `#${overflowCollapseID}`);
						navLink.setAttribute('data-bs-toggle', 'collapse');
						navLink.setAttribute('aria-expanded', 'false');
						navLink.setAttribute(
							'aria-controls',
							`#${overflowCollapseID}`,
						);
						navLink.removeAttribute('data-bs-auto-close');

						const dropdownMenu = lastChild.querySelector(
							'.dropdown-menu',
						) as HTMLDivElement;

						dropdownMenu.classList.add('collapse-menu', 'collapse');
						dropdownMenu.classList.remove('dropdown-menu');
						dropdownMenu.setAttribute('id', overflowCollapseID);
					}

					mainDesktopOverflowNav
						.querySelector('.dropdown-menu')
						.prepend(lastChild);
					mainDesktopNavHeight = mainDesktopNav.clientHeight;
				}
			}
		};

		window.addEventListener('load', createOverflowDropdown);
		window.addEventListener('resize', createOverflowDropdown);
		window.addEventListener('scroll', handleScroll);

		return () => {
			window.removeEventListener('load', createOverflowDropdown);
			window.removeEventListener('resize', createOverflowDropdown);
			window.removeEventListener('scroll', handleScroll);
		};
	}, []);

	const languageNav = (
		<ul className="nav justify-content-start calian-header__language-select-nav">
			<li className="nav-item dropdown">
				<a
					className="nav-link dropdown-toggle"
					href="#"
					aria-expanded="false"
					role="button"
					data-bs-toggle="dropdown"
					data-bs-auto-close="true"
				>
					{activeLanguage?.label}
				</a>
				{languageSelectionNav && languageSelectionNav.length > 0 && (
					<ul className="dropdown-menu">
						{languageSelectionNav.map((languageNavItem, index) => {
							return (
								<li key={languageNavItem.code + '-' + index}>
									<a
										className="dropdown-item"
										href={languageNavItem.href}
										target={
											languageNavItem.target
												? languageNavItem.target
												: '_self'
										}
									>
										{languageNavItem.label}
									</a>
								</li>
							);
						})}
					</ul>
				)}
			</li>
		</ul>
	);

	// End of sticky positioning code taken from default ba3e Navbar
	return (
		<header
			id={id}
			className={
				className ||
				cn(
					'calian-header calian-careers-header',
					invert && 'calian-header--invert',
					...classNames,
				)
			}
		>
			{topMenuNav && activeLanguage && (
				<div className="calian-header__topbar d-none d-xl-block border-bottom">
					<div className="calian-header__container px-3 container-fluid d-flex gap-3 justify-content-end">
						{topMenuNav && (
							<MMNav
								navItems={topMenuNav}
								collapseEnabled={false}
								dropdownEnabled={true}
								classNames={['calian-header__top-menu-nav']}
							/>
						)}
						{activeLanguage && languageNav}
					</div>
				</div>
			)}

			<div className="calian-header__container container-fluid">
				<nav
					className="calian-header__navbar navbar navbar-expand-xl navbar-white"
					ref={ref}
				>
					<div className="calian-header__navbar-container container-fluid">
						<a
							className="calian-header__navbar-brand navbar-brand"
							href={logoURL}
						>
							<Image {...logo} className="img-fluid" />
						</a>
						{headerBusinessUnit && (
							<p className="calian-header__business-unit">
								<a href={businessUnitURL}>
									{headerBusinessUnit}
								</a>
							</p>
						)}
						<div
							className="calian-header__navbar-collapse collapse collapse navbar-collapse"
							ref={collapseRef}
						>
							<div className="calian-header__navbar-left">
								{mainNav && (
									<>
										<Nav
											navItems={mainNav}
											collapseEnabled={false}
											dropdownEnabled={true}
											classNames={[
												'calian-header__main-nav',
											]}
										/>
										<ul className="calian-header__main-nav--overflow nav d-none more">
											<li className="nav-item dropdown">
												<a
													className="nav-link dropdown-toggle"
													href="#"
													aria-expanded="false"
													role="button"
													data-bs-toggle="dropdown"
													data-bs-auto-close="true"
												>
													{navLanguage === 'fr-ca'
														? 'Plus'
														: 'More'}
												</a>
												<ul className="dropdown-menu"></ul>
											</li>
										</ul>
									</>
								)}
							</div>
							<div className="calian-header__navbar-right">
								{activeLanguage && (
									<ul className="nav justify-content-start calian-header__language-select-nav">
										<li className="nav-item dropdown">
											<a
												className="nav-link dropdown-toggle"
												href="#"
												aria-expanded="false"
												role="button"
												data-bs-toggle="dropdown"
												data-bs-auto-close="true"
											>
												{activeLanguage?.label}
											</a>
											{languageSelectionNav &&
												languageSelectionNav.length >
													0 && (
													<ul className="dropdown-menu">
														{languageSelectionNav.map(
															(
																languageNavItem,
																index,
															) => {
																return (
																	<li
																		key={
																			languageNavItem.code +
																			'-' +
																			index
																		}
																	>
																		<a
																			className="dropdown-item"
																			href={
																				languageNavItem.href
																			}
																			target={
																				languageNavItem.target
																					? languageNavItem.target
																					: '_self'
																			}
																		>
																			{languageNavItem.icon && (
																				<i
																					className={
																						'icon ' +
																						languageNavItem.icon
																					}
																				></i>
																			)}
																			{
																				languageNavItem.label
																			}
																		</a>
																	</li>
																);
															},
														)}
													</ul>
												)}
										</li>
									</ul>
								)}
								{loginNav && (
									<Nav
										navItems={loginNav}
										collapseEnabled={false}
										dropdownEnabled={true}
										classNames={[
											'calian-header__login-select-nav',
										]}
									/>
								)}
							</div>
						</div>
					</div>
				</nav>
				<div className="calian-header__cta-search">
					{showAllButton && (
						<Button
							className={cn(
								`calian-header__cta-btn btn btn-sm btn-${
									styleInvert ? 'secondary' : 'primary'
								}`,
							)}
							{...showAllButton}
						/>
					)}
					<Button
						className="calian-header__search-btn btn"
						aria-label="Search"
						{...searchButton}
					>
						<i className="icon icon-magnifying-glass"></i>
					</Button>
				</div>
				<button
					type="button"
					className="calian-header__navbar-toggler btn navbar-toggler"
					data-bs-toggle="offcanvas"
					data-bs-target=".offcanvas"
					aria-label="Toggle"
				>
					<i className="icon icon-menu-alternate"></i>
				</button>
			</div>
			<div className="offcanvas offcanvas-end" ref={offcanvasRef}>
				<div className="offcanvas-header">
					<div className="calian-header__navbar-container container-fluid">
						<a
							className="calian-header__navbar-brand navbar-brand"
							href={logoURL}
						>
							<Image {...logo} className="img-fluid" />
						</a>
						{headerBusinessUnit && (
							<p className="calian-header__business-unit">
								<a href={businessUnitURL}>
									{headerBusinessUnit}
								</a>
							</p>
						)}
					</div>
					<button
						type="button"
						className="btn-close text-reset"
						data-bs-dismiss="offcanvas"
						aria-label="Close"
					></button>
				</div>
				<div className="offcanvas-body">
					<div className="account-info-container col-12 d-flex flex-row justify-content-start align-items-center position-relative">
						{profileLink && (
							<a
								className="account-profile-link d-flex flex-row-reverse align-items-center link-charcoal text-decoration-none fw-normal fs-7 position-absolute"
								href={profileLink.href}
							>
								{profileImage && (
									<Image
										{...profileImage}
										className="img-fluid"
										alt={
											profileImage.alt
												? profileImage.alt
												: 'Profile Image'
										}
									/>
								)}
							</a>
						)}
						{loginNav && (
							<Nav
								navItems={loginNav}
								vertical={true}
								collapseEnabled={true}
								dropdownEnabled={false}
								classNames={[
									profileLink
										? 'calian-careers-header__login-select-nav--mobile logged-in'
										: 'calian-careers-header__login-select-nav--mobile',
								]}
							/>
						)}
						{registerURL && (
							<a
								href={registerURL}
								className="calian-careers-header__register-link text-link-primary"
							>
								{registerURLText}
							</a>
						)}
					</div>
					{mainNav && (
						<Nav
							navItems={mainNav}
							vertical={true}
							collapseEnabled={true}
							dropdownEnabled={false}
							classNames={[
								'calian-careers-header__main-nav--mobile',
							]}
						/>
					)}
					{topMenuNav && (
						<Nav
							navItems={topMenuNav}
							collapseEnabled={true}
							dropdownEnabled={true}
							classNames={['calian-header__top-menu-nav--mobile']}
						/>
					)}

					<div className="calian-careers-header__cta-language-select-mobile container-fluid">
						<div className="row">
							<div className="col-6">
								{showAllButton && (
									<Button
										className={cn(
											`calian-careers-header__cta-btn--mobile btn btn-sm btn-${
												styleInvert
													? 'secondary'
													: 'primary'
											}`,
										)}
										{...showAllButton}
									/>
								)}
							</div>
							{activeLanguage && (
								<>
									<div className="col-6">
										<div className="calian-header__language-select-nav--mobile">
											<a
												className="nav-link collapse-toggle"
												data-bs-toggle="collapse"
												href="#navMobileCollapse"
												role="button"
												aria-expanded="false"
												aria-controls="navMobileCollapse"
											>
												{activeLanguage.label}
											</a>
										</div>
									</div>
									<div className="col-12">
										<div
											className="collapse"
											id="navMobileCollapse"
										>
											<div className="calian-header__language-select-nav--mobile--content">
												<ul className="nav flex-column">
													{languageSelectionNav.map(
														(
															languageNavItem,
															index,
														) => {
															return (
																<li
																	className="nav-item"
																	key={
																		languageNavItem.code +
																		'-' +
																		index
																	}
																>
																	<a
																		className="nav-link"
																		href={
																			languageNavItem.href
																		}
																		target={
																			languageNavItem.target
																				? languageNavItem.target
																				: '_self'
																		}
																	>
																		{languageNavItem.icon && (
																			<i
																				className={
																					'icon ' +
																					languageNavItem.icon
																				}
																			></i>
																		)}
																		{
																			languageNavItem.label
																		}
																	</a>
																</li>
															);
														},
													)}
												</ul>
											</div>
										</div>
									</div>
								</>
							)}
						</div>
						<div className="row">
							<div className="col-12 mt-7">
								{homeURLText && (
									<a
										className={cn(
											`calian-careers-header__home-btn d-flex justify-content-center align-items-center btn btn-sm btn-${
												styleInvert
													? 'secondary'
													: 'primary'
											} fs-8`,
										)}
										href={logoURL}
									>
										<i className="icon icon-caret-left-slim fs-9 me-3"></i>
										{homeURLText}
									</a>
								)}
							</div>
						</div>
					</div>
				</div>
			</div>
		</header>
	);
};

export default CareersHeader;
